import { RightPaneProps } from "./RightPane"
import {
  api_fetch_throws,
  DeductionDetailJson,
  DisputeJson,
  Integration,
  Message,
  Recipient,
  send_message,
  reply_to_thread,
  move_message_attachments_to_user_files,
  files_to_attachment_inputs,
} from "@/api"
import type { AttachedFile, EmailAttachmentInput } from "@/api"
import { has_ksolve_dispute_text, is_filed_in_ksolve } from "@/api/dispute.tsx"
import { classes, copyToClipboard, Id, unreachable, useAsyncEffect } from "@/utils"
import { useState, useEffect, useRef } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { ArrowUp, Check, ExternalLink, File, Expand, Paperclip, X } from "lucide-react"
import { toast } from "@/components/ui/use-toast"
import { triggerRefreshFlag } from "@/refresh_flag.tsx"
import { Textarea } from "@/components/ui/textarea"
import { LoadingSpinner } from "@/app/loading"
import { toastResult } from "@/toast"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer"
import { getFiles } from "@/deduction/[id]/UserFiles.tsx"
import type { DeductionFile } from "@/deduction/[id]/UserFiles.tsx"

type ConversationTab = "dispute" | "backup" | "bol-pod"

function getAvailableTabs(messages: Message[], dispute: DisputeJson | null): ConversationTab[] {
  const tabs: ConversationTab[] = []
  const disputeMessages = messages.filter(
    m => m.thread_kind !== "request-backup" && m.thread_kind !== "request-bol-pod" && m.thread_kind !== "check-request-forward"
  )
  const hasDispute = disputeMessages.length > 0 || (dispute && dispute.type !== "backup")
  if (hasDispute) tabs.push("dispute")
  const hasBackup = messages.some(m => m.thread_kind === "request-backup") || dispute?.type === "backup"
  const hasBolPod = messages.some(m => m.thread_kind === "request-bol-pod")
  if (hasBackup) tabs.push("backup")
  if (hasBolPod) tabs.push("bol-pod")
  return tabs
}

function getTabLabel(tab: ConversationTab): string {
  switch (tab) {
    case "dispute":
      return "Dispute"
    case "backup":
      return "Backup Request"
    case "bol-pod":
      return "BOL/POD Request"
  }
}

function filterMessagesByTab(messages: Message[], tab: ConversationTab): Message[] {
  switch (tab) {
    case "backup":
      return messages.filter(m => m.thread_kind === "request-backup")
    case "bol-pod":
      return messages.filter(m => m.thread_kind === "request-bol-pod")
    case "dispute":
      return messages.filter(m => m.thread_kind !== "request-backup" && m.thread_kind !== "request-bol-pod")
  }
}

/** Where a message came from, as the header shows it. A reply on a Dot Foods portal thread is filed in that portal. */
export function message_source_label(source: string): string {
  if (source === "freshdesk") return "Freshdesk"
  if (source === "unfi-portal") return "UNFI Portal"
  if (source === "dotfoods-portal") return "Dot Foods Portal"
  return "Email"
}

/**
 * True when the active tab's thread can be replied to. Every tab (dispute, backup request, BOL/POD request)
 * is a real email thread, and the "Write Bump Message" action fills this reply box, so a tab with messages
 * must always offer one. The exception is a UNFI Dispute Center thread: it is read from the portal and takes no
 * replies, so [UnfiPortalFollowupNotice] stands in for the box.
 */
export function should_show_reply_box(tab_messages: Message[]): boolean {
  return tab_messages.length > 0 && !is_unfi_portal_thread(tab_messages)
}

/** True when the conversation is on a UNFI Dispute Center thread, judged by its latest message. */
export function is_unfi_portal_thread(messages: Pick<Message, "source">[]): boolean {
  return messages[messages.length - 1]?.source === "unfi-portal"
}

/** True when the ConversationCard should show only the outside-MarginWiz placeholder (no synced messages). */
export function should_show_outside_marginwiz_placeholder(
  dispute: DisputeJson | null | undefined,
  message_count: number,
): boolean {
  return dispute != null && dispute.type === "unknown" && message_count === 0 && !has_ksolve_dispute_text(dispute)
}

export function normalize_message_text(value: string | null | undefined): string {
  if (!value) return ""
  return value
    .replace(/<[^>]*>/g, " ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
}

export function messages_overlap(left: string | null | undefined, right: string | null | undefined): boolean {
  const normalized_left = normalize_message_text(left)
  const normalized_right = normalize_message_text(right)
  if (!normalized_left || !normalized_right) return false
  return normalized_left.includes(normalized_right) || normalized_right.includes(normalized_left)
}

function get_message_body_for_overlap(message: Message): string {
  const text_body = (message as Message & { text_body?: string | null }).text_body
  return text_body ?? message.html_body
}

function has_overlap_with_synced_messages(dispute_message: string, messages: Message[]): boolean {
  return messages.some((message: Message) => messages_overlap(dispute_message, get_message_body_for_overlap(message)))
}

export function should_show_filed_dispute_summary(
  dispute: DisputeJson | null | undefined,
  messages: Message[],
  active_tab: ConversationTab,
): boolean {
  if (!dispute || !dispute.sent_at) return false
  if (dispute.type === "unknown" && !has_ksolve_dispute_text(dispute)) return false
  const dispute_tab: ConversationTab = dispute.type === "backup" ? "backup" : "dispute"
  if (active_tab !== dispute_tab) return false
  return !has_overlap_with_synced_messages(dispute.message, messages)
}

interface ConversationTabsProps {
  tabs: ConversationTab[]
  activeTab: ConversationTab
  onTabChange: (tab: ConversationTab) => void
}

function ConversationTabs({ tabs, activeTab, onTabChange }: ConversationTabsProps) {
  return (
    <Tabs value={activeTab} onValueChange={(value: string) => onTabChange(value as ConversationTab)}>
      <TabsList className="h-8 bg-orange-50 cursor-pointer">
        {tabs.map(tab => (
          <TabsTrigger
            key={tab}
            value={tab}
            className="text-xs px-3 py-1 data-[state=active]:bg-orange-500 data-[state=active]:text-white cursor-pointer">
            {getTabLabel(tab)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}

export function ConversationCard({ deduction }: RightPaneProps) {
  const availableTabs = getAvailableTabs(deduction.messages, deduction.dispute ?? null)
  const [activeTab, setActiveTab] = useState<ConversationTab>(availableTabs[0] ?? "dispute")
  const showTabs = availableTabs.length > 1

  let external
  if (deduction.dispute && deduction.source === "ahold") {
    let inner
    if (deduction.dispute.sent_at) {
      inner = <p class="text-sm text-gray-500">The dispute is filed in the Ahold portal.</p>
    } else {
      inner = <p class="text-sm text-red-800">There was an error filing the dispute.</p>
    }
    external = (
      <div class="flex flex-col items-center justify-between w-full space-y-2">
        {inner}
        <a
          href="https://ws1.aholdusa.com/jfitvp/rbs/open-new-case"
          class="border bg-white px-4 py-2 rounded-md"
          target="_blank">
          View in Ahold Portal
        </a>
      </div>
    )
  } else if (should_show_outside_marginwiz_placeholder(deduction.dispute, deduction.messages.length)) {
    external = (
      <span class="w-full italic text-gray-500 flex justify-center">
        This dispute was filed outside of MarginWiz, and we don't have any information about it.
      </span>
    )
  } else if (deduction.messages.length > 0 || deduction.dispute) {
    external = <ExternalMessages deduction={deduction} activeTab={activeTab} />
  }
  if (!external) {
    return null
  }
  return (
    <div id="conversation" class="border-t-6 border-orange-500 bg-orange-50 rounded-md p-4 space-y-2">
      <div class="flex items-center justify-between">
        <span class="flex">
          <div class="w-1 bg-orange-500 mr-2" />
          <h3 class="text-lg font-semibold leading-5">Conversation</h3>
        </span>
        {showTabs && <ConversationTabs tabs={availableTabs} activeTab={activeTab} onTabChange={setActiveTab} />}
      </div>
      {external}
    </div>
  )
}

interface ExternalMessagesProps {
  deduction: DeductionDetailJson
  activeTab: ConversationTab
  className?: string
}

function ExternalMessages({ deduction, activeTab, className }: ExternalMessagesProps) {
  const filteredMessages = filterMessagesByTab(deduction.messages, activeTab)
  const showReplyBox = should_show_reply_box(filteredMessages)

  let filedDispute
  let pendingDispute
  // Show pending/errored dispute card on the appropriate tab
  const disputeTab = deduction.dispute?.type === "backup" ? "backup" : "dispute"
  if (should_show_filed_dispute_summary(deduction.dispute, filteredMessages, activeTab)) {
    filedDispute = <FiledDisputeMessage dispute={deduction.dispute!} />
  }
  if (
    activeTab === disputeTab &&
    deduction.dispute &&
    deduction.dispute.type !== "unknown" &&
    !deduction.dispute.sent_at
  ) {
    if (deduction.dispute.error) {
      pendingDispute = (
        <div class="flex flex-col gap-2">
          <PendingDisputeMessage dispute={deduction.dispute} hasError />
          <span class="w-full italic text-red-500 flex justify-center">
            Error sending dispute: {deduction.dispute.error}
          </span>
        </div>
      )
    } else {
      pendingDispute = <PendingDisputeMessage dispute={deduction.dispute} />
    }
  }

  return (
    <div className={classes("flex flex-col gap-2", className)}>
      {filteredMessages.map((message, idx) => (
        <MessageItem
          key={message.id}
          message={message}
          expanded={idx === filteredMessages.length - 1}
          deductionId={deduction.id}
        />
      ))}
      {filedDispute}
      {pendingDispute}
      {showReplyBox && !!filteredMessages.length && (
        <SendMessageBox lastMessage={filteredMessages[filteredMessages.length - 1]} deduction={deduction} />
      )}
      {is_unfi_portal_thread(filteredMessages) && <UnfiPortalFollowupNotice deduction={deduction} />}
    </div>
  )
}

/** Stands in for the reply box on a UNFI Dispute Center thread. File Dispute decides from the claim's status. */
function UnfiPortalFollowupNotice({ deduction }: { deduction: DeductionDetailJson }) {
  return (
    <div class="flex items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-sm text-blue-900">
      <span>
        UNFI's Dispute Center doesn't take replies. Use File Dispute to follow up: a denied claim goes in as its
        appeal. A claim under review takes nothing new until UNFI answers.
      </span>
      <Button variant="outline" size="sm" className="shrink-0" onClick={() => DrawerDeduction.set(deduction)}>
        File Dispute
      </Button>
    </div>
  )
}

interface MessageItemProps {
  key: string
  message: Message
  expanded: boolean
  deductionId: string
}

function format_recipient(recipient: Recipient) {
  if (!recipient.name) {
    return recipient.email!
  }
  if (!recipient.email) {
    return recipient.name!
  } else {
    return `${recipient.name} <${recipient.email}>`
  }
}

function MessageItem({ message, expanded: initial, deductionId }: MessageItemProps) {
  let [expanded, setExpanded] = useState(initial)
  const formattedDate = format(new Date(message.date), "MMM d, yyyy")
  const [moving, setMoving] = useState(false)

  async function handleMoveAttachments() {
    if (!message.attachments?.length) return
    setMoving(true)
    try {
      const result = await move_message_attachments_to_user_files(deductionId, message.id)
      if (result.ok) {
        toast({
          title: "Success",
          description: `Moved ${result.value.data.length} file(s) to user files`,
        })
        triggerRefreshFlag()
      } else {
        toast({
          title: "Error",
          description: result.error.message,
          variant: "destructive",
        })
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to move attachments",
        variant: "destructive",
      })
    } finally {
      setMoving(false)
    }
  }

  let attachments
  if (message.attachments?.length) {
    attachments = (
      <div class="mt-3">
        <div class="flex items-center justify-between mb-2">
          <span class="text-gray-800 ">Attachments</span>
          <button
            onClick={handleMoveAttachments}
            disabled={moving}
            class="text-sm text-orange-600 hover:text-orange-800 disabled:opacity-50 disabled:cursor-not-allowed">
            {moving ? "Moving..." : "Move to User Files"}
          </button>
        </div>
        {message.attachments.map(a => (
          <div class="flex mt-2">
            <File className="-ml-1 w-8" />
            <a class="underline" href={`/api/_s3?s3_uri=${encodeURIComponent(a.s3_uri)}`} target="_blank">
              {a.name}
            </a>
          </div>
        ))}
      </div>
    )
  }

  function onClickHeader(e: MouseEvent) {
    if (e.altKey) {
      e.preventDefault()
      e.stopImmediatePropagation()
      copyToClipboard(message.id)
      toast({ title: "Copied to clipboard" })
      return
    }
    setExpanded(!expanded)
  }

  let first_letter = message.from.email?.[0] ?? message.from.name?.[0] ?? "?"

  let header
  if (expanded) {
    header = (
      <div onClick={onClickHeader} class="text-gray-500 text-sm h-10">
        <div class="flex items-center justify-between">
          <span class="">From: {format_recipient(message.from)}</span>
          <span class="text-nowrap">{formattedDate}</span>
        </div>
        <div class="flex justify-between">
          <span class="truncate">{message.subject}</span>
          {message_source_label(message.source)}
        </div>
      </div>
    )
  } else {
    header = (
      <div onClick={onClickHeader} class="text-gray-500 text-sm h-10">
        <div class="flex items-center justify-between">
          <span class="">From: {format_recipient(message.from)}</span>
          <span class="truncate">{message.subject}</span>
          <span class="text-nowrap">{formattedDate}</span>
        </div>
        <div class="flex">
          <div class="line-clamp-1 break-all grow" dangerouslySetInnerHTML={{ __html: message.html_body }} />
          {message_source_label(message.source)}
        </div>
      </div>
    )
  }
  return (
    <div class="bg-white p-3 rounded-md border-gray-100 border flex w-full">
      <div class="w-10 mt-1">
        <div
          class="flex items-center justify-center bg-orange-500 text-white rounded-full w-8 h-8 text-sm font-medium"
          onClick={onClickHeader}>
          {first_letter.toUpperCase()}
        </div>
      </div>
      <div class="w-full">
        {header}
        <div
          class={classes(
            "transition-all duration-300 ease-in-out overflow-hidden",
            expanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
          )}>
          <div dangerouslySetInnerHTML={{ __html: message.html_body }} class="mt-4 break-all" />
          {attachments}
        </div>
      </div>
    </div>
  )
}

interface PendingDisputeMessageProps {
  dispute: DisputeJson
  hasError?: boolean
}

function PendingDisputeMessage({ dispute, hasError }: PendingDisputeMessageProps) {
  const formattedDate = format(new Date(dispute.created_at), "MMM d, yyyy")

  return (
    <div
      class={classes(
        "bg-white p-3 rounded-md border-2 border-dashed flex w-full",
        hasError ? "border-red-300" : "border-orange-300"
      )}>
      <div class="w-10 mt-1">
        <div class="flex items-center justify-center bg-gray-400 text-white rounded-full w-8 h-8 text-sm font-medium">
          ?
        </div>
      </div>
      <div class="w-full">
        <div class="text-gray-500 text-sm">
          <div class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <span>{dispute.type === "backup" ? "Backup Request" : "Dispute"}</span>
              <span
                class={classes(
                  "text-xs px-2 py-0.5 rounded-full",
                  hasError ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"
                )}>
                {hasError ? "Failed" : "Pending"}
              </span>
            </span>
            <span class="text-nowrap">{formattedDate}</span>
          </div>
        </div>
        <div class="mt-2 text-gray-700 whitespace-pre-wrap">{filed_dispute_text(dispute)}</div>
      </div>
    </div>
  )
}

/** When the dispute was filed: the first K-Solve note for a dispute filed there, else our row's creation. */
export function filed_dispute_date(dispute: DisputeJson): Date {
  const first_note = dispute.metadata?.ksolve_notes?.[0]
  return parseISO(first_note?.created_on ?? dispute.created_at)
}

/** The text to show for a filed dispute; a K-Solve dispute whose text we have not read yet says so. */
export function filed_dispute_text(dispute: DisputeJson): string {
  if (is_filed_in_ksolve(dispute) && !has_ksolve_dispute_text(dispute)) {
    return "This dispute was filed directly in KeHE's K-Solve."
  }
  return dispute.message
}

function FiledDisputeMessage({ dispute }: { dispute: DisputeJson }) {
  const formattedDate = format(filed_dispute_date(dispute), "MMM d, yyyy")
  const filed_label = is_filed_in_ksolve(dispute) ? "Filed in K-Solve" : "Filed"

  return (
    <div class="bg-white p-3 rounded-md border border-gray-100 flex w-full">
      <div class="w-10 mt-1">
        <div class="flex items-center justify-center bg-green-600 text-white rounded-full w-8 h-8">
          <Check className="w-4 h-4" />
        </div>
      </div>
      <div class="w-full">
        <div class="text-gray-500 text-sm">
          <div class="flex items-center justify-between">
            <span class="flex items-center gap-2">
              <span>{dispute.type === "backup" ? "Backup Request" : "Dispute"}</span>
              <span class="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{filed_label}</span>
            </span>
            <span class="text-nowrap">{formattedDate}</span>
          </div>
        </div>
        <div class="mt-2 text-gray-700 whitespace-pre-wrap">{dispute.message}</div>
      </div>
    </div>
  )
}

interface SendMessageBoxProps {
  deduction: DeductionDetailJson
  lastMessage: Message
}

/// these are displayed to the user as tab names, so they should look nice
type SendMessageTab = "Freshdesk" | "Email"

interface SendMessageTabsProps {
  tabs: SendMessageTab[]
  activeTab: SendMessageTab
  setActiveTab: (tab: SendMessageTab) => void
}

function SendMessageTabs({ tabs, activeTab, setActiveTab }: SendMessageTabsProps) {
  return (
    <div class="flex border-b border-gray-200 mb-4">
      {tabs.map(tab => (
        <button
          class={`px-4 py-2 text-sm font-medium border-b-2 ${
            activeTab === tab
              ? "border-orange-500 text-orange-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab(tab)}>
          {tab}
        </button>
      ))}
    </div>
  )
}

function SendMessageBox({ lastMessage, deduction }: SendMessageBoxProps) {
  let states: SendMessageTab[] = lastMessage.source === "freshdesk" ? ["Freshdesk", "Email"] : ["Email"]
  const [activeTab, setActiveTab] = useState<SendMessageTab>(states[0])

  let tabs
  if (states.length > 1) {
    tabs = <SendMessageTabs tabs={states} activeTab={activeTab} setActiveTab={setActiveTab} />
  }
  let content
  if (activeTab === "Freshdesk") {
    let freshdeskTicketId = lastMessage.remote_id.split(":")[0]
    content = (
      <FreshdeskMessageBox
        threadId={lastMessage.thread_id}
        deduction={deduction}
        freshdeskTicketId={freshdeskTicketId!}
      />
    )
  } else if (activeTab === "Email") {
    let threadId
    if (lastMessage.source !== "freshdesk") {
      threadId = lastMessage.thread_id
    }
    content = <EmailMessageBox threadId={threadId} deduction={deduction} lastMessage={lastMessage} />
  } else {
    unreachable(activeTab)
  }

  return (
    <div className="w-full">
      {tabs}
      {content}
    </div>
  )
}

interface EmailMessageBoxProps {
  threadId?: Id
  deduction: DeductionDetailJson
  lastMessage: Message
}

interface TagInputProps {
  values: string[]
  onNewValue: (value: string) => void
  onDeleteValue: (value: string) => void
  label: string
}

interface AutocompleteInputProps {
  value: string
  onChange: (value: string) => void
  options: string[]
  label: string
  placeholder?: string
  class?: string
}

function TagInput(props: TagInputProps) {
  let { values, onNewValue, onDeleteValue, label } = props

  function handleKeyDown(e: KeyboardEvent | FocusEvent) {
    const target = e.target as HTMLInputElement
    if (!("key" in e) || e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault()
      const value = target.value.trim()
      if (!value) {
        return
      }
      onNewValue(value)
      target.value = ""
    } else if (e.key === "Backspace" && !target.value) {
      onDeleteValue(values[values.length - 1])
    }
  }

  return (
    <div className="flex flex-wrap gap-1 flex-1 min-h-8 p-1 border border-gray-100 rounded-md bg-white items-center">
      <label className="text-sm font-medium text-gray-700 px-1 py-1">{label}</label>
      {values.map(value => (
        <span
          key={value}
          className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm">
          {value}
          <button onClick={() => onDeleteValue(value)} className="text-orange-600 hover:text-orange-800">
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        className="flex-1 min-w-[120px] border-none outline-none focus:ring-0 focus:outline-none text-sm"
        onKeyDown={handleKeyDown}
        onBlur={handleKeyDown}
      />
    </div>
  )
}

function AutocompleteInput(props: AutocompleteInputProps) {
  let { value, onChange, options, label, placeholder } = props
  let [isOpen, setIsOpen] = useState(false)
  let [filteredOptions, setFilteredOptions] = useState<string[]>([])

  useEffect(() => {
    if (value) {
      let filtered = options.filter(option => option.toLowerCase().includes(value.toLowerCase()))
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions(options)
    }
  }, [value, options])

  function handleInputChange(e: any) {
    let newValue = e.target.value
    onChange(newValue)
    setIsOpen(true)
  }

  function handleOptionClick(option: string) {
    onChange(option)
    setIsOpen(false)
  }

  function handleInputFocus() {
    setIsOpen(true)
  }

  function handleInputBlur() {
    // Delay closing to allow option clicks
    setTimeout(() => setIsOpen(false), 150)
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault()
      if (filteredOptions.length > 0) {
        onChange(filteredOptions[0])
      }
      setIsOpen(false)
    } else if (e.key === "Escape") {
      setIsOpen(false)
    }
  }

  // Only allow valid options
  let isValidValue = options.includes(value)

  return (
    <div className="relative w-full">
      <div
        className={`flex items-center flex-1 border rounded-md bg-white overflow-hidden ${!isValidValue && value ? "border-red-300" : "border-gray-100"}`}>
        <label className="text-sm font-medium text-gray-700 px-2 py-1">{label}</label>
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 px-2 py-3 border-none outline-none text-sm focus:outline-none focus:ring-0"
        />
      </div>
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.map(option => (
            <div
              key={option}
              className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              onClick={() => handleOptionClick(option)}>
              {option}
            </div>
          ))}
        </div>
      )}
      {!isValidValue && value && (
        <div className="text-red-500 text-xs mt-1">Please select a valid option from the dropdown</div>
      )}
    </div>
  )
}

export function get_existing_email_attachment_options(deduction: DeductionDetailJson): DeductionFile[] {
  return getFiles(deduction).filter((file: DeductionFile) => {
    return (
      !!file.deduction_id ||
      file.s3_uri === deduction.backup_s3_uri ||
      file.s3_uri === deduction.check_s3_uri
    )
  })
}

interface EmailAttachmentPickerProps {
  attachments: AttachedFile[]
  setAttachments: (value: AttachedFile[] | ((attachments: AttachedFile[]) => AttachedFile[])) => void
  existingFiles: DeductionFile[]
  selectedExistingAttachmentUris: string[]
  setSelectedExistingAttachmentUris: (value: string[] | ((uris: string[]) => string[])) => void
  disabled?: boolean
}

function EmailAttachmentPicker({
  attachments,
  setAttachments,
  existingFiles,
  selectedExistingAttachmentUris,
  setSelectedExistingAttachmentUris,
  disabled,
}: EmailAttachmentPickerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showExistingFiles, setShowExistingFiles] = useState(false)
  const selectedExistingUris = new Set(selectedExistingAttachmentUris)

  function handleFileSelect(e: Event) {
    const target = e.target as HTMLInputElement
    const files = target.files
    if (!files) return
    const newAttachments: AttachedFile[] = Array.from(files).map((file: File) => ({
      file,
      id: crypto.randomUUID(),
    }))
    setAttachments((prev: AttachedFile[]) => [...prev, ...newAttachments])
    target.value = ""
  }

  function handleRemoveAttachment(id: string) {
    setAttachments((prev: AttachedFile[]) => prev.filter((attachment: AttachedFile) => attachment.id !== id))
  }

  function toggleExistingFile(s3_uri: string) {
    setSelectedExistingAttachmentUris((prev: string[]) => {
      if (prev.includes(s3_uri)) {
        return prev.filter((uri: string) => uri !== s3_uri)
      }
      return [...prev, s3_uri]
    })
  }

  function removeExistingFile(s3_uri: string) {
    setSelectedExistingAttachmentUris((prev: string[]) => prev.filter((uri: string) => uri !== s3_uri))
  }

  return (
    <div class="space-y-2">
      <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} class="hidden" />
      {attachments.length > 0 && (
        <div class="flex flex-wrap gap-2">
          {attachments.map(({ file, id }: AttachedFile) => (
            <span key={id} class="inline-flex max-w-full items-center gap-1 rounded bg-orange-100 px-2 py-1 text-sm text-orange-800">
              <File className="h-4 w-4 shrink-0" />
              <span class="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveAttachment(id)}
                disabled={disabled}
                class="text-orange-700 hover:text-orange-900 disabled:opacity-50">
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      {selectedExistingAttachmentUris.length > 0 && (
        <div class="flex flex-wrap gap-2">
          {existingFiles
            .filter((file: DeductionFile) => selectedExistingUris.has(file.s3_uri))
            .map((file: DeductionFile) => (
              <span
                key={file.s3_uri}
                class="inline-flex max-w-full items-center gap-1 rounded bg-blue-100 px-2 py-1 text-sm text-blue-800">
                <File className="h-4 w-4 shrink-0" />
                <span class="truncate">{file.display_name}</span>
                <button
                  type="button"
                  onClick={() => removeExistingFile(file.s3_uri)}
                  disabled={disabled}
                  class="text-blue-700 hover:text-blue-900 disabled:opacity-50">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
        </div>
      )}
      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          class="h-8 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
          <Paperclip className="h-4 w-4" />
          Attach files
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowExistingFiles((open: boolean) => !open)}
          disabled={disabled || existingFiles.length === 0}
          class="h-8 border-gray-200 bg-white text-gray-700 hover:bg-gray-50">
          <File className="h-4 w-4" />
          Use existing
        </Button>
      </div>
      {showExistingFiles && (
        <div class="max-h-64 w-[44rem] max-w-[calc(100vw-3rem)] overflow-y-auto rounded-md border border-gray-200 bg-white p-2 shadow-sm">
          {existingFiles.length === 0 ? (
            <p class="px-2 py-1 text-sm text-gray-500">No existing files on this deduction.</p>
          ) : (
            existingFiles.map((file: DeductionFile) => (
              <label
                key={file.s3_uri}
                class="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50">
                <Checkbox
                  checked={selectedExistingUris.has(file.s3_uri)}
                  onCheckedChange={() => toggleExistingFile(file.s3_uri)}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <span class="min-w-0 flex-1 break-words leading-5">{file.display_name}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function EmailMessageBox({ threadId, deduction }: EmailMessageBoxProps) {
  let [content, setContent] = useState("")
  let [loading, setLoading] = useState(false)
  let [expanded, setExpanded] = useState(false)
  let [fromRecipient, setFromRecipient] = useState<string>("")
  let [availableRecipients, setAvailableRecipients] = useState<string[]>([])
  let [toRecipients, setToRecipients] = useState<string[]>([])
  let [ccRecipients, setCcRecipients] = useState<string[]>([])
  let [bccRecipients, setBccRecipients] = useState<string[]>([])
  let [subject, setSubject] = useState("")
  let [attachments, setAttachments] = useState<AttachedFile[]>([])
  let [selectedExistingAttachmentUris, setSelectedExistingAttachmentUris] = useState<string[]>([])
  const existingFiles = get_existing_email_attachment_options(deduction)

  useAsyncEffect(async () => {
    let data = await api_fetch_throws<Integration[]>("/integration", { use_cache: true })
    data = data.filter(i => ["gmail", "microsoft"].includes(i.name))
    setAvailableRecipients(data.map(i => i.username))
    setFromRecipient(data[0].username)
  }, [])

  async function handleSendMessage() {
    if (!content) return
    setLoading(true)
    let attachmentInputs: EmailAttachmentInput[]
    try {
      attachmentInputs = await files_to_attachment_inputs(attachments)
    } catch (error) {
      setLoading(false)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to read attachments",
        variant: "destructive",
      })
      return
    }

    // Simple reply to existing thread - use the dedicated endpoint
    if (threadId && !expanded) {
      let res = await reply_to_thread(
        deduction.id,
        threadId,
        content,
        attachmentInputs,
        selectedExistingAttachmentUris
      )
      setLoading(false)
      toastResult(res, "Message sent")
      if (res.ok) {
        setContent("")
        setAttachments(() => [])
        setSelectedExistingAttachmentUris([])
        triggerRefreshFlag()
      }
      return
    }

    // Composing new email or reply with custom recipients - validate sender
    if (!availableRecipients.includes(fromRecipient)) {
      toast({
        title: "Invalid sender",
        description: "Please select a valid sender from the dropdown",
        variant: "destructive",
      })
      setLoading(false)
      return
    }

    let body = {
      thread_id: threadId,
      text: content,
      from: fromRecipient,
      to: toRecipients,
      cc: ccRecipients,
      bcc: bccRecipients,
      subject: subject || null,
      attachments: attachmentInputs,
      existing_attachment_s3_uris: selectedExistingAttachmentUris,
    }
    let res = await send_message(deduction, body)
    setLoading(false)
    toastResult(res, "Message sent")
    if (res.ok) {
      setContent("")
      setAttachments(() => [])
      setSelectedExistingAttachmentUris([])
      triggerRefreshFlag()
    }
  }

  return (
    <div>
      <div class="space-y-1">
        <div class="flex justify-end">
          <button onClick={() => setExpanded(!expanded)} className="p-1 hover:text-orange-400 rounded">
            <Expand className="w-4 h-4" />
          </button>
        </div>
        {(expanded || !threadId) && (
          <>
            <div className="flex items-center gap-2">
              <AutocompleteInput
                value={fromRecipient}
                onChange={setFromRecipient}
                options={availableRecipients}
                label="From"
                placeholder="Select sender..."
              />
            </div>
            <TagInput
              values={toRecipients}
              onNewValue={v => setToRecipients(vs => [...vs, v])}
              onDeleteValue={v => setToRecipients(vs => vs.filter(w => w !== v))}
              label="To"
            />
            <TagInput
              values={ccRecipients}
              onNewValue={v => setCcRecipients(vs => [...vs, v])}
              onDeleteValue={v => setCcRecipients(vs => vs.filter(w => w !== v))}
              label="Cc"
            />
            <TagInput
              values={bccRecipients}
              onNewValue={v => setBccRecipients(vs => [...vs, v])}
              onDeleteValue={v => setBccRecipients(vs => vs.filter(w => w !== v))}
              label="Bcc"
            />
            {/* Subject field */}
            <div className="flex items-center gap-2">
              <div className="flex items-center flex-1 border border-gray-100 rounded-md bg-white">
                <label className="text-sm font-medium text-gray-700 px-2 py-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e: any) => setSubject(e.target.value)}
                  className="flex-1 px-2 py-3 border-none outline-none text-sm focus:outline-none focus:ring-0"
                />
              </div>
            </div>
          </>
        )}
      </div>
      <Textarea
        value={content}
        onChange={(e: any) => setContent(e.target.value)}
        onKeyDown={async (e: any) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault()
            await handleSendMessage()
          }
        }}
        className="rounded-md border border-gray-100 focus:ring-0 resize-none w-full mt-1 py-0 pt-4 pb-0 text-md focus-visible:outline-hidden focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-2"
        placeholder="Send reply..."
        data-ai-message-target="true"
      />
      <div class="mt-2 flex items-end justify-between gap-2">
        <EmailAttachmentPicker
          attachments={attachments}
          setAttachments={setAttachments}
          existingFiles={existingFiles}
          selectedExistingAttachmentUris={selectedExistingAttachmentUris}
          setSelectedExistingAttachmentUris={setSelectedExistingAttachmentUris}
          disabled={loading}
        />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <button
            class="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            onClick={handleSendMessage}
            disabled={content.length === 0 || !availableRecipients.includes(fromRecipient)}>
            Send <ArrowUp className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>
    </div>
  )
}

interface FreshdeskMessageBoxProps {
  threadId: Id
  deduction: DeductionDetailJson
  freshdeskTicketId: string
}

function FreshdeskMessageBox({ threadId, deduction, freshdeskTicketId }: FreshdeskMessageBoxProps) {
  let [content, setContent] = useState("")
  let [loading, setLoading] = useState(false)

  async function handleSendMessage() {
    if (!content) return
    setLoading(true)
    let res = await reply_to_thread(deduction.id, threadId, content)
    setLoading(false)
    toastResult(res, "Message sent")
    if (res.ok) {
      setContent("")
      triggerRefreshFlag()
    }
  }

  return (
    <div>
      <Textarea
        value={content}
        onChange={(e: any) => setContent(e.target.value)}
        onKeyDown={async (e: any) => {
          if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
            e.preventDefault()
            await handleSendMessage()
          }
        }}
        className="rounded-md border border-gray-100 focus:ring-0 resize-none w-full py-0 pt-4 pb-0 text-md focus-visible:outline-hidden focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-2"
        placeholder="Send reply..."
        data-ai-message-target="true"
      />
      <div className="flex justify-end items-center gap-2 mt-1">
        <a
          href={`https://kehecx.freshdesk.com/support/tickets/${freshdeskTicketId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
          <ExternalLink className="h-4 w-4" />
          Attach files in Freshdesk
        </a>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <button
            class="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md flex items-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            onClick={handleSendMessage}
            disabled={content.length === 0}>
            Send <ArrowUp className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const minimal_unknown_dispute = { type: "unknown" } as DisputeJson
  const sent_dispute = {
    type: "chargeback",
    sent_at: "2026-04-24T00:00:00.000Z",
    message: "Please reverse this chargeback amount",
  } as DisputeJson
  const backup_sent_dispute = {
    type: "backup",
    sent_at: "2026-04-24T00:00:00.000Z",
    message: "Please provide backup documentation",
  } as DisputeJson
  const pending_dispute = {
    type: "chargeback",
    sent_at: null,
    message: "Please reverse this chargeback amount",
  } as DisputeJson

  const ksolve_dispute = {
    type: "unknown",
    sent_at: "2026-09-25T00:00:00.000Z",
    created_at: "2026-09-25T00:00:00.000Z",
    message: "Please refund the 2% fee of $73.16.",
    metadata: { filed_in: "ksolve", ksolve_notes: [{ id: 1, created_by: "Kevin", created_on: "2026-09-21T23:17:20.307" }] },
  } as unknown as DisputeJson

  describe("filed_dispute_text", () => {
    it("shows the K-Solve text once read", () => {
      expect(filed_dispute_text(ksolve_dispute)).toBe("Please refund the 2% fee of $73.16.")
    })

    it("hides the sync's placeholder", () => {
      const placeholder = { ...ksolve_dispute, message: "created externally, directly in ksolve", metadata: {} } as DisputeJson
      expect(filed_dispute_text(placeholder)).toBe("This dispute was filed directly in KeHE's K-Solve.")
    })

    it("dates a K-Solve dispute by its first note", () => {
      expect(format(filed_dispute_date(ksolve_dispute), "yyyy-MM-dd")).toBe("2026-09-21")
    })
  })

  function make_message(html_body: string): Message {
    return { html_body } as Message
  }

  describe("should_show_outside_marginwiz_placeholder", () => {
    it("is true for unknown dispute with no messages", () => {
      expect(should_show_outside_marginwiz_placeholder(minimal_unknown_dispute, 0)).toBe(true)
    })

    it("is false for unknown dispute when there are synced messages", () => {
      expect(should_show_outside_marginwiz_placeholder(minimal_unknown_dispute, 1)).toBe(false)
    })

    it("is false when there is no dispute", () => {
      expect(should_show_outside_marginwiz_placeholder(null, 0)).toBe(false)
      expect(should_show_outside_marginwiz_placeholder(undefined, 0)).toBe(false)
    })

    it("is false for non-unknown dispute types", () => {
      expect(should_show_outside_marginwiz_placeholder({ type: "chargeback" } as DisputeJson, 0)).toBe(false)
    })

    it("is false for a K-Solve dispute whose text we read", () => {
      expect(should_show_outside_marginwiz_placeholder(ksolve_dispute, 0)).toBe(false)
    })
  })

  describe("messages_overlap", () => {
    it("matches when one message contains the other", () => {
      expect(messages_overlap("Please reverse this chargeback amount", "reverse this chargeback")).toBe(true)
    })

    it("matches in either direction", () => {
      expect(messages_overlap("reverse this chargeback", "Please reverse this chargeback amount")).toBe(true)
    })

    it("normalizes html and whitespace before matching", () => {
      expect(messages_overlap("Please reverse this chargeback amount", "<p> Please reverse this chargeback amount </p>")).toBe(true)
    })

    it("returns false when there is no overlap", () => {
      expect(messages_overlap("Please reverse this chargeback amount", "Need proof of delivery for invoice")).toBe(false)
    })
  })

  describe("should_show_filed_dispute_summary", () => {
    it("is true for sent disputes with no overlapping synced messages", () => {
      expect(should_show_filed_dispute_summary(sent_dispute, [make_message("Different message body")], "dispute")).toBe(true)
    })

    it("is false for sent disputes with overlapping synced messages", () => {
      expect(
        should_show_filed_dispute_summary(
          sent_dispute,
          [make_message("<div>Please reverse this chargeback amount and confirm.</div>")],
          "dispute",
        ),
      ).toBe(false)
    })

    it("is false for pending disputes", () => {
      expect(should_show_filed_dispute_summary(pending_dispute, [make_message("Different message body")], "dispute")).toBe(false)
    })

    it("is false for unknown disputes", () => {
      const unknown_sent_dispute = { type: "unknown", sent_at: "2026-04-24T00:00:00.000Z", message: "hello" } as DisputeJson
      expect(should_show_filed_dispute_summary(unknown_sent_dispute, [], "dispute")).toBe(false)
    })

    it("is true for an unknown K-Solve dispute whose text we read", () => {
      expect(should_show_filed_dispute_summary(ksolve_dispute, [], "dispute")).toBe(true)
    })

    it("is false for an unknown K-Solve dispute still on the placeholder", () => {
      const placeholder = { ...ksolve_dispute, message: "created externally, directly in ksolve" } as DisputeJson
      expect(should_show_filed_dispute_summary(placeholder, [], "dispute")).toBe(false)
    })

    it("is false when there is no dispute", () => {
      expect(should_show_filed_dispute_summary(null, [], "dispute")).toBe(false)
      expect(should_show_filed_dispute_summary(undefined, [], "dispute")).toBe(false)
    })

    it("shows backup filed disputes on backup tab only", () => {
      expect(should_show_filed_dispute_summary(backup_sent_dispute, [make_message("Different message body")], "backup")).toBe(true)
      expect(should_show_filed_dispute_summary(backup_sent_dispute, [make_message("Different message body")], "dispute")).toBe(false)
    })
  })

  describe("message_source_label", () => {
    it("names each portal and falls back to email", () => {
      expect(message_source_label("dotfoods-portal")).toBe("Dot Foods Portal")
      expect(message_source_label("unfi-portal")).toBe("UNFI Portal")
      expect(message_source_label("freshdesk")).toBe("Freshdesk")
      expect(message_source_label("gmail")).toBe("Email")
    })
  })

  describe("should_show_reply_box", () => {
    const dispute_message = { thread_kind: null, html_body: "Please reverse this chargeback" } as unknown as Message
    const backup_request = { thread_kind: "request-backup", html_body: "Please provide backup" } as Message
    const bol_pod_request = { thread_kind: "request-bol-pod", html_body: "Please send the POD" } as Message

    it("offers no reply box on a UNFI Dispute Center thread, which takes none", () => {
      const portal_message = { source: "unfi-portal", thread_kind: null } as unknown as Message
      expect(is_unfi_portal_thread([portal_message])).toBe(true)
      expect(should_show_reply_box([portal_message])).toBe(false)
    })

    it("still offers one on a UNFI dispute that went by email", () => {
      const emailed = { source: "gmail", thread_kind: null } as unknown as Message
      expect(is_unfi_portal_thread([emailed])).toBe(false)
      expect(should_show_reply_box([emailed])).toBe(true)
    })

    it("shows the reply box on a dispute thread", () => {
      expect(should_show_reply_box([dispute_message])).toBe(true)
    })

    it("shows the reply box on a backup request thread so it can be bumped", () => {
      expect(should_show_reply_box([backup_request])).toBe(true)
    })

    it("shows the reply box on a BOL/POD request thread", () => {
      expect(should_show_reply_box([bol_pod_request])).toBe(true)
    })

    it("hides the reply box when the tab has no messages to reply to", () => {
      expect(should_show_reply_box([])).toBe(false)
    })
  })

  describe("get_existing_email_attachment_options", () => {
    it("includes deduction uploads, canonical backup, and check files", () => {
      const deduction = {
        id: "deduction-1",
        source: "kehe",
        customer_name: "KeHE",
        user_files: [
          {
            display_name: "Uploaded backup.pdf",
            s3_uri: "s3://bucket/org/brand/user_files/deduction/deduction-1/uploaded.pdf",
            date: "2026-06-18T00:00:00.000Z",
            actor_name: "Test User",
          },
        ],
        backup_s3_uri: "s3://bucket/org/brand/backups/backup.pdf",
        check_s3_uri: "s3://bucket/org/brand/checks/check.pdf",
        distributor_backup_s3_uris: ["s3://bucket/org/brand/portal/extra-backup.pdf"],
      } as DeductionDetailJson

      const options = get_existing_email_attachment_options(deduction)
      expect(options.map((file: DeductionFile) => file.s3_uri)).toEqual([
        "s3://bucket/org/brand/user_files/deduction/deduction-1/uploaded.pdf",
        "s3://bucket/org/brand/backups/backup.pdf",
        "s3://bucket/org/brand/checks/check.pdf",
      ])
    })
  })
}
